<template>
  <div
    class="preg"
    :class="[tone === 'admin' ? 'preg--admin' : 'preg--seller', { 'preg--sale': mallKind === 'SALE' }]"
  >
    <div class="preg__main">
      <header class="preg__intro">
        <h2 class="preg__title">{{ isNew ? '상품 등록' : '상품 수정' }}</h2>
        <p class="preg__lead">새로운 상품을 등록하고, 쇼핑몰에 노출할 수 있습니다.</p>
      </header>

      <section class="preg__card">
        <h3 class="preg__card-title">기본 정보</h3>

        <div class="preg__field">
          <span class="preg__label">판매 구분 <em>*</em></span>
          <div class="preg__kinds" role="radiogroup" aria-label="판매 구분">
            <label class="preg__kind" :class="{ 'is-on': mallKind === 'REGULAR' }">
              <input v-model="mallKind" type="radio" value="REGULAR" />
              <strong>상시몰</strong>
              <span>항상 판매</span>
            </label>
            <label class="preg__kind preg__kind--sale is-locked" :class="{ 'is-on': mallKind === 'SALE' }">
              <input v-model="mallKind" type="radio" value="SALE" disabled />
              <strong>공동구매몰</strong>
              <span>잠시 등록 중단</span>
            </label>
          </div>
          <p class="preg__hint">지금은 공동구매몰에 상품을 올릴 수 없습니다. 상시몰만 등록됩니다.</p>
          <p v-if="mallKind === 'REGULAR'" class="preg__hint">
            1개 판매가는 묶음 금액을 계산하는 기준입니다. 쇼핑몰에는 아래에서 고른 구성의 총액이 먼저 보입니다.
          </p>
          <p v-else class="preg__hint">이미 공동구매인 상품만 그 구분을 유지합니다. 새로 공동구매로 바꾸지는 못합니다.</p>
        </div>

        <label class="preg__field">
          <span class="preg__label">상품명 <em>*</em></span>
          <input
            v-model="form.name"
            class="preg__input"
            maxlength="150"
            placeholder="예: [본사정품] 올인원 멀티비타민 이뮨샷 7개입, 8박스"
          />
          <span class="preg__hint">고객이 보는 제목입니다. 개수·박스·회분은 상품명에 직접 적으세요.</span>
        </label>

        <label class="preg__field">
          <span class="preg__label">카테고리 <em>*</em></span>
          <select v-model="form.category" class="preg__input">
            <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
          </select>
        </label>

        <div class="preg__field" data-tour="seller-product-origin">
          <span class="preg__label">원산지 <em v-if="originMust">*</em></span>
          <div class="preg__radios" role="radiogroup" aria-label="원산지">
            <label v-for="item in originKinds" :key="item.id || 'none'" class="preg__radio">
              <input v-model="originKind" type="radio" :value="item.id" />
              {{ item.label }}
            </label>
          </div>
          <label v-if="originKind === 'IMPORT'" class="preg__origin-detail">
            <span class="preg__label">국가명 <em>*</em></span>
            <input
              v-model="originDetail"
              class="preg__input"
              maxlength="60"
              placeholder="예: 미국"
              aria-label="수입 국가명"
            />
          </label>
          <label v-if="originKind === 'MIXED'" class="preg__origin-detail">
            <span class="preg__label">국가별 원산지 <em>*</em></span>
            <textarea
              v-model="originDetail"
              class="preg__input preg__textarea"
              rows="2"
              maxlength="160"
              placeholder="예: 쌀 국내산, 밀 미국산"
              aria-label="국가별 원산지"
            />
          </label>
          <label v-if="originKind === 'OTHER'" class="preg__origin-detail">
            <span class="preg__label">원산지 내용 <em>*</em></span>
            <input
              v-model="originDetail"
              class="preg__input"
              maxlength="80"
              placeholder="표시할 원산지를 적으세요"
              aria-label="기타 원산지"
            />
          </label>
          <p class="preg__hint">{{ originHint }}</p>
        </div>

        <div v-if="canEditStatus" class="preg__field">
          <span class="preg__label">상품 상태 <em>*</em></span>
          <div ref="statusRadioRoot" class="preg__radios" role="radiogroup" aria-label="상품 상태">
            <label v-for="item in saleStatusChoices" :key="item.value" class="preg__radio">
              <input
                type="radio"
                :name="statusRadioName"
                :value="item.value"
                :checked="saleStatus === item.value"
                @click="pickSaleStatus(item.value, $event)"
                @keydown.space="pickSaleStatus(item.value, $event)"
              />
              {{ item.label }}
            </label>
          </div>
        </div>

        <label class="preg__field">
          <span class="preg__label">상품 설명</span>
          <textarea
            v-model="form.description"
            class="preg__input preg__textarea"
            maxlength="4000"
            rows="5"
            placeholder="상품에 대한 상세 설명을 입력하세요."
          />
        </label>
      </section>

      <section class="preg__card">
        <h3 class="preg__card-title">이미지 등록</h3>
        <div class="preg__images">
          <div v-for="(url, index) in form.images" :key="url + index" class="preg__image">
            <img :src="url" alt="" />
            <span v-if="index === 0" class="preg__image-badge">대표 이미지</span>
            <button type="button" class="preg__image-del" aria-label="이미지 삭제" @click="removeImage(index)">
              ×
            </button>
          </div>
          <button
            v-if="form.images.length < maxImages"
            type="button"
            class="preg__image preg__image--add"
            :disabled="uploading"
            @click="pickImages"
          >
            <span>+</span>
            이미지 추가
            <small>(최대 {{ maxImages }}장)</small>
          </button>
        </div>
        <p class="preg__note">권장 이미지 사이즈 800 × 800px 이상 · 파일 형식 JPG, PNG (최대 10MB)</p>
        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          hidden
          @change="onFiles"
        />
      </section>

      <section class="preg__card">
        <h3 class="preg__card-title">가격</h3>
        <div class="preg__grid preg__grid--price">
          <label class="preg__field">
            <span class="preg__label">공급가 <em>*</em></span>
            <span class="preg__unit">
              <input v-model.number="form.cost" class="preg__input" type="number" min="0" />
              <em>원</em>
            </span>
          </label>
          <label class="preg__field">
            <span class="preg__label">{{ mallKind === 'REGULAR' ? '1개 판매가' : '1개 가격' }} <em>*</em></span>
            <span class="preg__unit">
              <input v-model.number="form.price" class="preg__input" type="number" min="0" />
              <em>원</em>
            </span>
          </label>
          <label class="preg__field">
            <span class="preg__label">마진 배분율</span>
            <span class="preg__unit">
              <input v-model.number="form.marginShare" class="preg__input" type="number" min="0" max="100" />
              <em>%</em>
            </span>
          </label>
        </div>
        <ul v-if="priceSplit.ready" class="preg__split" :class="{ 'is-over': priceSplit.over }">
          <li v-for="row in priceSplit.rows" :key="row.key">
            <div class="preg__split-meta">
              <i :class="row.tone" />
              <span>{{ row.label }}</span>
              <strong>{{ money(row.amount) }}</strong>
              <em>{{ row.rate }}%</em>
            </div>
            <div class="preg__split-track" role="img" :aria-label="`${row.label} ${money(row.amount)}`">
              <span :class="row.tone" :style="{ width: row.width }" />
            </div>
          </li>
        </ul>
      </section>

      <section class="preg__card" data-tour="product-editor-sell">
        <div class="preg__card-head">
          <h3 class="preg__card-title">{{ mallKind === 'SALE' ? '카톤' : '구매 수량' }}</h3>
        </div>

        <template v-if="mallKind === 'SALE'">
          <label class="preg__field">
            <span class="preg__label">1카톤 개수 <em>*</em></span>
            <span class="preg__unit">
              <input v-model.number="form.packQty" class="preg__input" type="number" min="1" />
              <em>개</em>
            </span>
            <span class="preg__hint">
              한 카톤에 들어 있는 개수입니다. 목록에는 1개 가격이 먼저 보이고, 상품을 열면 카톤 가격과 이 개수 묶음이 보입니다. 최소 주문은 1카톤입니다.
            </span>
          </label>
          <p v-if="sellingPrice > 0" class="preg__hint">
            1개당 {{ priceText }} · 1카톤 {{ cartonPriceText }} · {{ cartonPieces }}개 묶음
          </p>
        </template>

        <template v-else>
          <p class="preg__hint">
            구성마다 고객이 내는 총액을 적습니다. 전면으로 고른 가격이 목록과 상품 화면에 먼저 보입니다. 1개 구성은 없어도 됩니다.
          </p>
          <ul class="preg__opt-list">
            <li
              v-for="row in qtyRows"
              :key="row.opt.key"
              class="preg__qty-row"
              :class="{ 'is-front': row.opt.front }"
            >
              <label class="preg__front">
                <input
                  type="radio"
                  name="preg-storefront"
                  :checked="row.opt.front === true"
                  :aria-label="`${row.opt.name}을 쇼핑몰 전면에 표시`"
                  @change="setFront(row.opt)"
                />
                전면
              </label>
              <strong>{{ row.opt.name }}</strong>
              <label v-if="row.qty === 1" class="preg__qty-price">{{ priceText }}</label>
              <label v-else class="preg__qty-price">
                <input
                  :value="row.total"
                  type="number"
                  min="0"
                  :aria-label="`${row.opt.name} 금액`"
                  @input="onBundlePrice(row.opt, $event)"
                />
                <em>원</em>
              </label>
              <span class="preg__opt-note">{{ row.note }}</span>
              <button
                type="button"
                class="preg__text-btn"
                :aria-label="`${row.opt.name} 삭제`"
                @click="removeOption(row.opt)"
              >
                삭제
              </button>
            </li>
          </ul>
          <div class="preg__qty-add">
            <button
              v-for="count in presetQtys"
              :key="count"
              type="button"
              :disabled="hasQty(count)"
              @click="addQty(count)"
            >
              {{ count }}개
            </button>
            <label class="preg__qty-custom">
              <input v-model.number="customQty" type="number" min="1" aria-label="추가할 개수" />
              <em>개</em>
              <button type="button" @click="addQty(customQty)">추가</button>
            </label>
          </div>
        </template>
      </section>

      <section class="preg__card">
        <h3 class="preg__card-title">재고</h3>
        <div class="preg__field">
          <span class="preg__label">재고 수량 <em>*</em></span>
          <template v-if="stockLocked">
            <p class="preg__locked">재고 {{ form.stock.toLocaleString('ko-KR') }}{{ sellNoun }}</p>
            <button type="button" class="preg__text-btn" @click="emit('open-stock-history')">재고·이력에서 추가</button>
          </template>
          <span v-else class="preg__unit">
            <input v-model.number="form.stock" class="preg__input" type="number" min="0" />
            <em>{{ sellNoun }}</em>
          </span>
        </div>
      </section>

      <section class="preg__card" data-tour="seller-product-detail">
        <div class="preg__card-head">
          <h3 class="preg__card-title">상세 설명</h3>
          <span class="preg__hint">{{ form.blocks.length }}개</span>
        </div>
        <p class="preg__hint">올린 이미지가 상품 화면 본문에 위에서부터 순서대로 보입니다.</p>
        <button
          type="button"
          class="preg__drop"
          :class="{ 'is-over': detailDragOver }"
          :disabled="uploading"
          @click="addDetailImages"
          @dragover.prevent="detailDragOver = true"
          @dragleave="detailDragOver = false"
          @drop.prevent="onDetailDrop"
        >
          <AppIcon :icon="uploading ? 'mdi-progress-upload' : 'mdi-tray-arrow-up'" :size="28" />
          <span>{{ uploading ? '이미지를 올리는 중입니다' : '본문 이미지 올리기' }}</span>
          <small>클릭하거나 이미지를 끌어다 놓으세요. 여러 장 가능</small>
        </button>
        <p v-if="!form.blocks.length" class="preg__note">이미지가 본문입니다. 글이 필요하면 소제목·문단·안내를 아래에 더합니다.</p>
        <ol v-else class="preg__blocks">
          <li v-for="(block, index) in form.blocks" :key="block.key" class="preg__block">
            <div class="preg__block-head">
              <strong>{{ blockLabel(block.type) }}</strong>
              <div class="preg__block-tools">
                <button type="button" aria-label="위로" :disabled="index === 0" @click="moveBlock(index, -1)">
                  <AppIcon icon="mdi-arrow-up" :size="16" />
                </button>
                <button
                  type="button"
                  aria-label="아래로"
                  :disabled="index === form.blocks.length - 1"
                  @click="moveBlock(index, 1)"
                >
                  <AppIcon icon="mdi-arrow-down" :size="16" />
                </button>
                <button type="button" aria-label="블록 삭제" @click="removeBlock(index)">
                  <AppIcon icon="mdi-trash-can-outline" :size="16" />
                </button>
              </div>
            </div>
            <input
              v-if="block.type === 'HEADING'"
              v-model="block.text"
              class="preg__input"
              maxlength="80"
              placeholder="소제목을 입력하세요"
            />
            <textarea
              v-else-if="block.type === 'TEXT' || block.type === 'NOTICE'"
              v-model="block.text"
              class="preg__input preg__textarea"
              :rows="block.type === 'NOTICE' ? 2 : 4"
              maxlength="2000"
              :placeholder="block.type === 'NOTICE' ? '안내 문구를 입력하세요' : '본문 문단을 입력하세요'"
            />
            <div v-else class="preg__block-image">
              <button
                v-if="!block.imageUrl"
                type="button"
                class="preg__drop preg__drop--slim"
                :disabled="uploading"
                @click="pickBlockImage(block.key)"
              >
                <AppIcon icon="mdi-tray-arrow-up" :size="22" />
                <span>이미지 올리기</span>
              </button>
              <div v-else class="preg__block-thumb">
                <img :src="block.imageUrl" alt="" />
              </div>
              <div class="preg__block-image-side">
                <button
                  v-if="block.imageUrl"
                  type="button"
                  class="preg__upload-btn"
                  :disabled="uploading"
                  @click="pickBlockImage(block.key)"
                >
                  <AppIcon icon="mdi-tray-arrow-up" :size="16" />
                  이미지 교체
                </button>
                <input v-model="block.alt" class="preg__input" maxlength="150" placeholder="이미지 설명을 입력하세요" />
              </div>
            </div>
          </li>
        </ol>
        <div class="preg__block-add">
          <button type="button" @click="addBlock('HEADING')">소제목</button>
          <button type="button" @click="addBlock('TEXT')">문단</button>
          <button type="button" @click="addDetailImages">이미지 올리기</button>
          <button type="button" @click="addBlock('NOTICE')">안내</button>
        </div>
      </section>

      <section class="preg__card">
        <h3 class="preg__card-title">배송 정보</h3>
        <div class="preg__grid">
          <div class="preg__field">
            <span class="preg__label">배송비</span>
            <p class="preg__locked">무료배송</p>
            <span class="preg__hint">고객에게 배송비를 따로 받지 않습니다. 판매가격에 포함합니다.</span>
          </div>
          <div class="preg__field">
            <span class="preg__label">출고 소요 <em>*</em></span>
            <div class="preg__lead-picks">
              <button
                v-for="lead in leadTimes"
                :key="lead"
                type="button"
                :class="{ 'is-on': form.leadTime === lead }"
                @click="form.leadTime = lead"
              >
                {{ lead }}
              </button>
            </div>
            <input
              v-model="form.leadTime"
              class="preg__input"
              type="text"
              maxlength="30"
              placeholder="직접 입력"
              aria-label="출고 소요"
            />
          </div>
          <div class="preg__field">
            <span class="preg__label">환불 시 배송비 <em>*</em></span>
            <div class="preg__return">
              <span class="preg__unit">
                <input v-model.number="form.returnEvery" class="preg__input" type="number" min="1" />
                <em>{{ returnNoun }}당</em>
              </span>
              <span class="preg__unit">
                <input v-model.number="form.returnFee" class="preg__input" type="number" min="0" />
                <em>원</em>
              </span>
            </div>
          </div>
        </div>
      </section>

      <section class="preg__card">
        <h3 class="preg__card-title">관리자 메모</h3>
        <label class="preg__field">
          <span class="preg__label">검수에 남길 말</span>
          <textarea
            v-model="form.requestNote"
            class="preg__input preg__textarea"
            rows="3"
            maxlength="500"
            placeholder="검수 때 참고할 말을 남기세요. 선택입니다."
          />
        </label>
      </section>
    </div>

    <aside class="preg__preview" data-tour="seller-product-preview-map">
      <div class="preg__preview-tabs" role="tablist" aria-label="상품 미리보기">
        <button
          type="button"
          role="tab"
          class="preg__preview-tab"
          :class="{ 'is-on': previewTab === 'card' }"
          :aria-selected="previewTab === 'card'"
          @click="previewTab = 'card'"
        >
          목록 카드
        </button>
        <button
          type="button"
          role="tab"
          class="preg__preview-tab"
          :class="{ 'is-on': previewTab === 'buy' }"
          :aria-selected="previewTab === 'buy'"
          @click="previewTab = 'buy'"
        >
          구매 화면
        </button>
      </div>
      <div v-show="previewTab === 'card'" class="preg__preview-pane" role="tabpanel">
        <ProductCard :product="previewProduct" mode="static" />
      </div>
      <div v-show="previewTab === 'buy'" class="preg__preview-pane" role="tabpanel">
        <MallProductBuyPanel :product="previewProduct" />
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import type { UploadedImage } from '@/api/mallProduct'
import AppIcon from '@/components/common/AppIcon.vue'
import MallProductBuyPanel from '@/components/mall/MallProductBuyPanel.vue'
import ProductCard from '@/components/mall/ProductCard.vue'
import type { MallDetailBlock } from '@/constants/mallProducts'
import {
  BLOCK_LABEL,
  LEAD_TIMES,
  MAX_PRODUCT_IMAGES,
  ORIGIN_KIND_OPTIONS,
  composeOrigin,
  applySaleCarton,
  marginOf,
  nextRowKey,
  originRequiredFor,
  toPreviewProduct,
  type EditorOption,
  type OriginKind,
  type ProductEditorForm,
} from '@/constants/productEditor'
import { optionPieceCount, sellUnitNoun } from '@/utils/mallSell'

type MallKind = 'REGULAR' | 'SALE'

const presetQtys = [2, 3, 5, 10]

const props = withDefaults(
  defineProps<{
    form: ProductEditorForm
    tone?: 'seller' | 'admin'
    storeName?: string
    categories: string[]
    uploader: (files: File[]) => Promise<UploadedImage[]>
    storeShipping: { returnFee: number; cutoff: string; returnPolicy: string }
    storeShippingReadonly?: boolean
    stockLocked?: boolean
    saleStatus?: string
    canEditStatus?: boolean
    isNew?: boolean
  }>(),
  {
    tone: 'seller',
    storeName: '',
    storeShippingReadonly: false,
    stockLocked: false,
    saleStatus: '',
    canEditStatus: false,
    isNew: true,
  },
)

const emit = defineEmits<{
  notify: [message: string]
  'open-stock-history': []
  'update-status': [status: 'SELLING' | 'SOLDOUT' | 'HIDDEN']
}>()

const saleStatusChoices = [
  { value: 'SELLING', label: '판매중' },
  { value: 'SOLDOUT', label: '품절' },
  { value: 'HIDDEN', label: '판매중지' },
] as const

const statusRadioName = `preg-sale-status-${Math.random().toString(36).slice(2, 8)}`
const statusRadioRoot = ref<HTMLElement | null>(null)

function syncSaleStatusRadios() {
  const root = statusRadioRoot.value
  if (!root) return
  root.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach((input) => {
    input.checked = input.value === props.saleStatus
  })
}

function pickSaleStatus(status: 'SELLING' | 'SOLDOUT' | 'HIDDEN', event: Event) {
  event.preventDefault()
  syncSaleStatusRadios()
  if (props.saleStatus === status) return
  emit('update-status', status)
}

watch(
  () => props.saleStatus,
  async () => {
    await nextTick()
    syncSaleStatusRadios()
  },
)

const form = props.form
const fileInput = ref<HTMLInputElement | null>(null)
const uploading = ref(false)
const blockImageKey = ref<number | 'new' | null>(null)
const detailDragOver = ref(false)
const maxImages = MAX_PRODUCT_IMAGES
const leadTimes = LEAD_TIMES
const mallKind = computed<MallKind>({
  get() {
    return form.mallKind === 'REGULAR' ? 'REGULAR' : 'SALE'
  },
  set(value) {
    form.saleCode = ''
    /* 공동구매몰 신규 등록은 잠시 막는다. 이미 공동구매인 상품은 구분을 유지한다. */
    if (value === 'SALE') return
    form.mallKind = 'REGULAR'
    form.orderStep = 1
    form.minOrder = 1
  },
})

const originKinds = ORIGIN_KIND_OPTIONS
const originMust = computed(() => originRequiredFor(form.category))
const originKind = computed<OriginKind>({
  get: () => form.originKind || '',
  set(value) {
    if (form.originKind !== value) form.originDetail = ''
    form.originKind = value
    form.origin = composeOrigin(form.originKind, form.originDetail)
  },
})
const originDetail = computed({
  get: () => form.originDetail,
  set(value: string) {
    form.originDetail = value
    form.origin = composeOrigin(form.originKind, form.originDetail)
  },
})
const originHint = computed(() =>
  originMust.value
    ? '식품·건강은 원산지를 골라야 검수를 요청할 수 있습니다. 수입산은 국가명, 혼합은 국가별 원산지를 적습니다.'
    : '이 분류는 원산지를 비워 둘 수 있습니다. 표시해야 하는 품목만 고르세요.',
)
const sellNoun = computed(() => sellUnitNoun(form.sellUnit))
const returnNoun = computed(() => '개')
const cartonPieces = computed(() => Math.max(1, Math.trunc(Number(form.packQty) || 1)))
const cartonPriceText = computed(() => money(Math.max(0, sellingPrice.value) * cartonPieces.value))
const sellerSliceLabel = computed(() => props.storeName.trim() || '업체')
const sellingPrice = computed(() => Math.max(0, Number(form.price) || 0))
const priceText = computed(() => money(sellingPrice.value))
const priceSplit = computed(() => {
  const price = sellingPrice.value
  const cost = Math.max(0, Number(form.cost) || 0)
  const share = Math.min(100, Math.max(0, Number(form.marginShare) || 0))
  const margin = marginOf({ price, cost, marginShare: share })
  const over = price > 0 && cost > price
  const seller = over ? 0 : Math.max(0, margin.seller)
  const platform = over ? 0 : Math.max(0, margin.platform)
  const rateOf = (amount: number) => (price > 0 ? Math.round((amount / price) * 100) : 0)
  const widthOf = (amount: number) => `${Math.min(100, Math.max(0, (amount / Math.max(price, 1)) * 100))}%`
  return {
    ready: price > 0,
    over,
    rows: [
      {
        key: 'cost',
        tone: 'is-cost',
        label: '공급가',
        amount: cost,
        rate: rateOf(cost),
        width: over ? '100%' : widthOf(Math.min(cost, price)),
      },
      {
        key: 'seller',
        tone: 'is-seller',
        label: sellerSliceLabel.value,
        amount: seller,
        rate: rateOf(seller),
        width: widthOf(seller),
      },
      {
        key: 'platform',
        tone: 'is-platform',
        label: '함우리',
        amount: platform,
        rate: rateOf(platform),
        width: widthOf(platform),
      },
    ],
  }
})
const customQty = ref<number | ''>('')

function money(value: number) {
  return `${Math.round(value).toLocaleString('ko-KR')}원`
}

function bundleTotal(opt: EditorOption) {
  return Math.max(0, sellingPrice.value + (Number(opt.extra) || 0))
}

function bundleNote(qty: number | null, total: number) {
  if (qty === 1) return '1개 기준가와 같습니다'
  if (!qty) return '개수로 시작하는 이름만 묶음으로 계산합니다'
  return `개당 ${money(Math.round(total / qty))}`
}

const qtyRows = computed(() =>
  form.options
    .filter((opt) => opt.use !== false && opt.name.trim())
    .map((opt) => {
      const qty = optionPieceCount(opt.name)
      const total = bundleTotal(opt)
      return { opt, qty, total, note: bundleNote(qty, total) }
    })
    .sort((a, b) => (a.qty ?? 9999) - (b.qty ?? 9999) || a.opt.name.localeCompare(b.opt.name, 'ko')),
)

function hasQty(count: number) {
  return form.options.some((opt) => opt.use !== false && optionPieceCount(opt.name) === count)
}

function setFront(opt: EditorOption) {
  for (const row of form.options) row.front = row.key === opt.key
}

function addQty(raw: number | '') {
  const count = Math.floor(Number(raw) || 0)
  if (count < 1) {
    emit('notify', '1개 이상부터 넣을 수 있습니다.')
    return
  }
  if (count > 999) return
  if (hasQty(count)) return
  const unit = sellingPrice.value
  const group =
    form.options.find((opt) => opt.use !== false && opt.name.trim())?.group.trim() || '구성'
  const alone = !form.options.some((opt) => opt.use !== false && opt.name.trim())
  form.options.push({
    key: nextRowKey(),
    group,
    name: `${count}개`,
    extra: count === 1 ? 0 : unit * (count - 1),
    stock: 0,
    use: true,
    front: alone,
  })
  customQty.value = ''
}

function onBundlePrice(opt: EditorOption, event: Event) {
  if (optionPieceCount(opt.name) === 1) return
  const raw = (event.target as HTMLInputElement).value
  const total = raw === '' ? 0 : Math.max(0, Math.round(Number(raw) || 0))
  opt.extra = total - sellingPrice.value
}

const previewTab = ref<'card' | 'buy'>('card')

const previewProduct = computed(() => {
  const product = toPreviewProduct(form, {
    storeName: props.storeName,
    storeCutoff: props.storeShipping.cutoff,
    storeReturnFee: props.storeShipping.returnFee,
    storeReturnPolicy: props.storeShipping.returnPolicy,
  })
  product.soldOut = props.saleStatus === 'SOLDOUT' || (product.stock ?? 0) <= 0
  return product
})

function removeOption(opt: EditorOption) {
  const index = form.options.findIndex((row) => row.key === opt.key)
  if (index >= 0) form.options.splice(index, 1)
}

function removeImage(index: number) {
  form.images.splice(index, 1)
}

function pickImages() {
  if (uploading.value) return
  blockImageKey.value = null
  fileInput.value?.click()
}

function pickBlockImage(key: number) {
  if (uploading.value) return
  blockImageKey.value = key
  fileInput.value?.click()
}

function addDetailImages() {
  if (uploading.value) return
  blockImageKey.value = 'new'
  fileInput.value?.click()
}

function onDetailDrop(event: DragEvent) {
  detailDragOver.value = false
  const files = Array.from(event.dataTransfer?.files ?? []).filter((file) => file.type.startsWith('image/'))
  if (!files.length || uploading.value) return
  void uploadDetailImages(files)
}

function blockLabel(type: MallDetailBlock['type']) {
  return BLOCK_LABEL[type]
}

function addBlock(type: MallDetailBlock['type']) {
  form.blocks.push({ key: nextRowKey(), type, text: '', imageUrl: '', alt: '' })
}

function moveBlock(index: number, step: number) {
  const next = index + step
  if (next < 0 || next >= form.blocks.length) return
  const [moved] = form.blocks.splice(index, 1)
  form.blocks.splice(next, 0, moved)
}

function removeBlock(index: number) {
  form.blocks.splice(index, 1)
}

async function onFiles(event: Event) {
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files || [])
  input.value = ''
  const target = blockImageKey.value
  blockImageKey.value = null
  if (!files.length) return
  if (target === 'new') {
    await uploadDetailImages(files)
    return
  }
  const room = target == null ? maxImages - form.images.length : 1
  if (room <= 0) {
    emit('notify', `이미지는 ${maxImages}장까지 올릴 수 있습니다.`)
    return
  }
  uploading.value = true
  try {
    const uploaded = await props.uploader(files.slice(0, room))
    if (typeof target === 'number') {
      const block = form.blocks.find((item) => item.key === target)
      if (block && uploaded[0]?.url) block.imageUrl = uploaded[0].url
      return
    }
    for (const image of uploaded) {
      if (image.url && form.images.length < maxImages) form.images.push(image.url)
    }
  } catch (error) {
    emit('notify', error instanceof Error ? error.message : '이미지를 올리지 못했습니다.')
  } finally {
    uploading.value = false
  }
}

async function uploadDetailImages(files: File[]) {
  if (!files.length || uploading.value) return
  uploading.value = true
  try {
    const uploaded = await props.uploader(files.slice(0, 20))
    let count = 0
    for (const image of uploaded) {
      if (!image.url) continue
      form.blocks.push({ key: nextRowKey(), type: 'IMAGE', text: '', imageUrl: image.url, alt: '' })
      count += 1
    }
    if (count) emit('notify', `본문 이미지 ${count}장을 올렸습니다.`)
  } catch (error) {
    emit('notify', error instanceof Error ? error.message : '이미지를 올리지 못했습니다.')
  } finally {
    uploading.value = false
  }
}

function lockFreeShipping() {
  if (form.shippingFee !== 0) form.shippingFee = 0
  if (form.shippingCharge !== 'ONCE') form.shippingCharge = 'ONCE'
}

watch(() => form.shippingFee, lockFreeShipping)
watch(() => form.shippingCharge, lockFreeShipping)

watch(
  () => form.packQty,
  () => {
    if (mallKind.value !== 'SALE') return
    applySaleCarton(form)
  },
)

watch(sellingPrice, (next, prev) => {
  if (prev == null || next === prev) return
  for (const opt of form.options) {
    const qty = optionPieceCount(opt.name)
    if (qty === 1) {
      opt.extra = 0
      continue
    }
    const extra = Number(opt.extra) || 0
    if (prev <= 0 && extra === 0 && qty && qty > 1) {
      opt.extra = next * (qty - 1)
      continue
    }
    opt.extra = Math.round(prev + extra) - next
  }
})

watch(
  () => `${mallKind.value}|${form.options.map((opt) => `${opt.name}:${opt.use}`).join(',')}`,
  () => {
    if (mallKind.value !== 'REGULAR') return
    const live = form.options.filter((opt) => opt.use !== false && opt.name.trim())
    const pieces = live.filter((opt) => optionPieceCount(opt.name) != null)
    if (pieces.length === 1) pieces[0].front = true
    if (!pieces.length || pieces.length !== live.length) return
    for (const opt of pieces) {
      if (opt.stock) opt.stock = 0
    }
  },
  { immediate: true },
)

onMounted(() => {
  lockFreeShipping()
  if (form.marginShare == null || Number.isNaN(Number(form.marginShare))) form.marginShare = 50
  if (form.mallKind !== 'REGULAR' && form.mallKind !== 'SALE') {
    form.mallKind = 'REGULAR'
  }
})
</script>

<style lang="scss" src="./ProductRegisterSheet.scss"></style>
